import { useState, type ChangeEvent } from 'react';
import type { AnyFieldMeta } from '@tanstack/react-form';
import { Link } from '@tanstack/react-router';
import Decimal from 'decimal.js';
import { Button } from '../../../components/ui/Button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../../components/ui/Dialog';
import { FormField, Input } from '../../../components/ui/FormField';
import { FormModal } from '@repo/web-form';
import { useFormModal } from '@repo/form';
import { useToast } from '../../../components/ui/Toast';
import { z } from 'zod';
import {
  useUpdateScheme,
  useSubmitScheme,
  useSuspendScheme,
  useResumeScheme,
  useCloseScheme,
  useActivateScheme,
  useAdvancePeriod,
  useTerminateScheme,
  useMarkSchemeReady,
  useCompleteScheme,
} from '../network/schemes.api';

interface SchemeStatusActionsScheme {
  id: string;
  name?: string;
  displayName?: string;
  branchId?: string;
  productTypeId?: string;
  chitValue?: number;
  subscriberCount?: number;
  durationCycles?: number;
  cycleFrequency?: 'MONTHLY' | 'FORTNIGHTLY' | 'WEEKLY';
  auctionType?: 'OPEN' | 'CLOSED';
  auctionDayOfMonth?: number;
  startDate?: string;
  tdsApplicable?: boolean;
  gstApplicable?: boolean;
  dividendCategory?: 'ESTIMATE_BASED' | 'AUCTION_BASED' | 'DRAW_BASED' | null;
  hasForemanTicket?: boolean;
  biddingWindowMinutes?: number | null;
  currentCycle?: number;
  status: string;
}

interface SchemeStatusActionsProps {
  scheme: SchemeStatusActionsScheme;
}

// Financial terms are owned by the scheme template and snapshotted at creation — only
// non-financial identity fields stay editable here, and only while still in DRAFT.
const editValidator = z.object({
  displayName: z.string().min(1, 'Display name is required'),
});

export function SchemeStatusActions({ scheme }: SchemeStatusActionsProps) {
  const { toast } = useToast();
  const [editOpen, setEditOpen] = useState(false);
  const [suspendOpen, setSuspendOpen] = useState(false);
  const [activateOpen, setActivateOpen] = useState(false);
  const [closeOpen, setCloseOpen] = useState(false);
  const [terminateOpen, setTerminateOpen] = useState(false);

  const [suspendReason, setSuspendReason] = useState('');
  const [terminateReason, setTerminateReason] = useState('');
  const [closeConfirmText, setCloseConfirmText] = useState('');
  const [startDate, setStartDate] = useState('');
  const [auctionDay, setAuctionDay] = useState(scheme.auctionDayOfMonth || 10);

  const submitMutation = useSubmitScheme(scheme.id);
  const suspendMutation = useSuspendScheme(scheme.id);
  const resumeMutation = useResumeScheme(scheme.id);
  const closeMutation = useCloseScheme(scheme.id);
  const updateMutation = useUpdateScheme(scheme.id);
  const activateMutation = useActivateScheme(scheme.id);
  const advancePeriodMutation = useAdvancePeriod(scheme.id);
  const terminateMutation = useTerminateScheme(scheme.id);
  const markReadyMutation = useMarkSchemeReady(scheme.id);
  const completeMutation = useCompleteScheme(scheme.id);

  const editModal = useFormModal(
    {
      defaultValues: {
        displayName: scheme.name || scheme.displayName || '',
      },
      schema: editValidator,
    },
    updateMutation,
    {
      onSuccess: () => {
        setEditOpen(false);
        toast({ type: 'success', title: 'Success', description: 'Scheme updated successfully.' });
      },
      onError: (err: unknown) => {
        const message = err instanceof Error ? err.message : 'Failed to update scheme.';
        toast({ type: 'error', title: 'Error', description: message });
      },
    }
  );

  const handleSubmit = async () => {
    try {
      await submitMutation.mutateAsync();
      toast({ type: 'success', title: 'Success', description: 'Scheme submitted successfully.' });
    // allow-silent-catch: error is surfaced to the user via toast(), not console/logger
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to submit scheme.';
      toast({ type: 'error', title: 'Error', description: message });
    }
  };

  const handleMarkReady = async () => {
    try {
      await markReadyMutation.mutateAsync();
      toast({ type: 'success', title: 'Success', description: 'Scheme marked READY for review.' });
    // allow-silent-catch: error is surfaced to the user via toast(), not console/logger
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to mark scheme READY.';
      toast({ type: 'error', title: 'Error', description: message });
    }
  };

  const handleAdvancePeriod = async () => {
    try {
      await advancePeriodMutation.mutateAsync();
      toast({ type: 'success', title: 'Success', description: 'Period advanced.' });
    // allow-silent-catch: error is surfaced to the user via toast(), not console/logger
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to advance the period.';
      toast({ type: 'error', title: 'Error', description: message });
    }
  };

  const handleComplete = async () => {
    try {
      await completeMutation.mutateAsync();
      toast({ type: 'success', title: 'Success', description: 'Scheme completed.' });
    // allow-silent-catch: error is surfaced to the user via toast(), not console/logger
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to complete scheme.';
      toast({ type: 'error', title: 'Error', description: message });
    }
  };

  const handleSuspend = async () => {
    if (!suspendReason) return;
    try {
      await suspendMutation.mutateAsync({ reason: suspendReason });
      setSuspendOpen(false);
      setSuspendReason('');
      toast({ type: 'success', title: 'Success', description: 'Scheme suspended successfully.' });
    // allow-silent-catch: error is surfaced to the user via toast(), not console/logger
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to suspend scheme.';
      toast({ type: 'error', title: 'Error', description: message });
    }
  };

  const handleResume = async () => {
    try {
      await resumeMutation.mutateAsync();
      toast({ type: 'success', title: 'Success', description: 'Scheme resumed successfully.' });
    // allow-silent-catch: error is surfaced to the user via toast(), not console/logger
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to resume scheme.';
      toast({ type: 'error', title: 'Error', description: message });
    }
  };

  const handleClose = async () => {
    if (closeConfirmText !== 'CLOSE') return;
    try {
      await closeMutation.mutateAsync();
      setCloseOpen(false);
      setCloseConfirmText('');
      toast({ type: 'success', title: 'Success', description: 'Scheme closed successfully.' });
    // allow-silent-catch: error is surfaced to the user via toast(), not console/logger
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to close scheme.';
      toast({ type: 'error', title: 'Error', description: message });
    }
  };

  const handleTerminate = async () => {
    if (!terminateReason.trim()) return;
    try {
      await terminateMutation.mutateAsync({ reason: terminateReason.trim() });
      setTerminateOpen(false);
      setTerminateReason('');
      toast({ type: 'success', title: 'Success', description: 'Scheme terminated.' });
    // allow-silent-catch: error is surfaced to the user via toast(), not console/logger
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to terminate scheme.';
      toast({ type: 'error', title: 'Error', description: message });
    }
  };

  const handleActivate = async () => {
    if (!startDate || !auctionDay) return;
    try {
      await activateMutation.mutateAsync({ startDate, auctionDayOfMonth: new Decimal(auctionDay).toNumber() });
      setActivateOpen(false);
      setStartDate('');
      toast({ type: 'success', title: 'Success', description: 'Scheme activated successfully.' });
    // allow-silent-catch: error is surfaced to the user via toast(), not console/logger
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to activate scheme.';
      toast({ type: 'error', title: 'Error', description: message });
    }
  };

  const fe = (meta: AnyFieldMeta & { touchedErrors?: unknown[] }) => {
    return meta.touchedErrors && meta.touchedErrors.length > 0
      ? String(meta.touchedErrors[0])
      : undefined;
  };

  const canAdvance =
    scheme.status === 'ACTIVE' &&
    scheme.currentCycle !== undefined &&
    scheme.durationCycles !== undefined &&
    scheme.currentCycle < scheme.durationCycles;

  return (
    <div className="flex items-center gap-3">
      {/* Edit Action — non-financial fields only, DRAFT or PENDING_REGISTRATION */}
      {(scheme.status === 'DRAFT' || scheme.status === 'PENDING_REGISTRATION') && (
        <Button variant="secondary" onClick={() => setEditOpen(true)}>
          Edit
        </Button>
      )}

      {/* Submit Action */}
      {scheme.status === 'DRAFT' && (
        <Button onClick={handleSubmit} disabled={submitMutation.isPending} variant="primary">
          Submit for Registration
        </Button>
      )}

      {/* Registrar approval is a real maker!=checker step through the shared approvals
          engine — a different staff member must approve/reject it from the Approvals queue. */}
      {scheme.status === 'PENDING_REGISTRATION' && (
        <Link to="/approvals" className="text-sm text-accent-600 hover:text-accent-500 hover:underline font-medium">
          Awaiting registrar approval — review in Approvals queue →
        </Link>
      )}

      {/* Mark READY: optional inspectable-readiness step after registration (FU-10) */}
      {scheme.status === 'REGISTERED' && (
        <Button onClick={handleMarkReady} disabled={markReadyMutation.isPending} variant="secondary">
          Mark READY
        </Button>
      )}

      {/* Activate Action (from REGISTERED directly, or after review in READY) */}
      {(scheme.status === 'REGISTERED' || scheme.status === 'READY') && (
        <Button onClick={() => setActivateOpen(true)} disabled={activateMutation.isPending} variant="primary">
          Activate
        </Button>
      )}

      {/* Advance Period (FU-10): rolls the group into its next cycle once the current one is done */}
      {canAdvance && (
        <Button onClick={handleAdvancePeriod} disabled={advancePeriodMutation.isPending} variant="secondary">
          Advance Period ({scheme.currentCycle}/{scheme.durationCycles})
        </Button>
      )}

      {/* Suspend / Close Actions */}
      {scheme.status === 'ACTIVE' && (
        <>
          <Button onClick={() => setSuspendOpen(true)} variant="secondary" className="border-warning-500 text-warning-500 hover:bg-warning-50">
            Suspend
          </Button>
          <Button onClick={() => setCloseOpen(true)} disabled={closeMutation.isPending} variant="danger">
            Close
          </Button>
        </>
      )}

      {/* Complete Scheme: natural completion after all cycles finish */}
      {scheme.status === 'ACTIVE' &&
        scheme.currentCycle !== undefined &&
        scheme.durationCycles !== undefined &&
        scheme.currentCycle >= scheme.durationCycles && (
          <Button onClick={handleComplete} disabled={completeMutation.isPending} variant="primary">
            Complete Scheme
          </Button>
      )}

      {/* Resume Action */}
      {scheme.status === 'SUSPENDED' && (
        <>
          <Button onClick={handleResume} disabled={resumeMutation.isPending} variant="primary">
            Resume
          </Button>
          <Button onClick={() => setTerminateOpen(true)} variant="danger">
            Terminate
          </Button>
        </>
      )}

      {/* Terminate also available while ACTIVE or CLOSING — exceptional wind-down (FU-10) */}
      {(scheme.status === 'ACTIVE' || scheme.status === 'CLOSING') && (
        <Button onClick={() => setTerminateOpen(true)} variant="ghost" className="text-danger-500 hover:text-danger-700">
          Terminate
        </Button>
      )}

      {/* Edit Modal — displayName only; financial terms live on the template snapshot */}
      <FormModal
        open={editOpen}
        onClose={() => setEditOpen(false)}
        title="Edit Scheme"
        modal={editModal}
        submitLabel="Save Changes"
      >
        <div className="space-y-1.5">
          <editModal.form.Field
            name="displayName"
            children={(field: { state: { value: string; meta: unknown }; name: string; handleBlur: () => void; handleChange: (val: string) => void }) => (
              <FormField label="Display Name" required error={fe(field.state.meta as never)}>
                <Input
                  value={field.state.value}
                  onBlur={field.handleBlur}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => field.handleChange(e.target.value)}
                  disabled={editModal.form.state.isSubmitting}
                />
              </FormField>
            )}
          />
          <p className="text-xs text-text-muted">Financial terms come from the scheme template and cannot be edited here.</p>
        </div>
      </FormModal>

      {/* Suspend Modal */}
      <Dialog open={suspendOpen} onOpenChange={setSuspendOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Suspend Scheme</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-4 py-4">
            <p className="text-sm text-text-secondary">
              Please enter the reason for suspending this scheme.
            </p>
            <FormField label="Reason" htmlFor="suspendReason" required>
              <Input
                id="suspendReason"
                value={suspendReason}
                onChange={(e: ChangeEvent<HTMLInputElement>) => setSuspendReason(e.target.value)}
                placeholder="Reason for suspension..."
              />
            </FormField>
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setSuspendOpen(false)}>Cancel</Button>
            <Button
              variant="danger"
              onClick={handleSuspend}
              disabled={!suspendReason || suspendMutation.isPending}
            >
              Confirm Suspend
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Terminate Modal — exceptional termination with mandatory reason */}
      <Dialog open={terminateOpen} onOpenChange={(open) => { setTerminateOpen(open); if (!open) setTerminateReason(''); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Terminate Scheme</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-4 py-4">
            <p className="text-sm text-text-secondary">
              Exceptional early wind-down without the closure checklist. This permanently ends the
              scheme and cannot be undone. A written reason is mandatory.
            </p>
            <FormField label="Termination Reason" htmlFor="terminateReason" required>
              <Input
                id="terminateReason"
                value={terminateReason}
                onChange={(e: ChangeEvent<HTMLInputElement>) => setTerminateReason(e.target.value)}
                placeholder="Why is this scheme being terminated?"
              />
            </FormField>
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setTerminateOpen(false)}>Cancel</Button>
            <Button
              variant="danger"
              onClick={handleTerminate}
              disabled={!terminateReason.trim() || terminateMutation.isPending}
            >
              Confirm Terminate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Close Confirmation Modal — the closure checklist/approval flow lives in the Closure tab. */}
      <Dialog open={closeOpen} onOpenChange={(open) => { setCloseOpen(open); if (!open) setCloseConfirmText(''); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Close Scheme</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-4 py-4">
            <p className="text-sm text-text-secondary">
              This permanently closes the scheme and cannot be undone. All cycles, auctions, and
              subscriber activity for this chit fund will stop. Type <strong>CLOSE</strong> to confirm.
            </p>
            <FormField label="Confirmation" htmlFor="closeConfirmText" required>
              <Input
                id="closeConfirmText"
                value={closeConfirmText}
                onChange={(e: ChangeEvent<HTMLInputElement>) => setCloseConfirmText(e.target.value)}
                placeholder="Type CLOSE to confirm"
              />
            </FormField>
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setCloseOpen(false)}>Cancel</Button>
            <Button
              variant="danger"
              onClick={handleClose}
              disabled={closeConfirmText !== 'CLOSE' || closeMutation.isPending}
            >
              Confirm Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Activate Modal */}
      <Dialog open={activateOpen} onOpenChange={setActivateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Activate Scheme</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-4 py-4">
            <p className="text-sm text-text-secondary">
              Please enter the commencement date and the recurring auction day to activate this scheme.
            </p>
            <FormField label="Commencement Date" htmlFor="startDate" required>
              <Input
                id="startDate"
                type="date"
                value={startDate}
                onChange={(e: ChangeEvent<HTMLInputElement>) => setStartDate(e.target.value)}
              />
            </FormField>
            <FormField label="Auction Day of Month (1-28)" htmlFor="auctionDay" required>
              <Input
                id="auctionDay"
                type="number"
                min="1"
                max="28"
                value={auctionDay}
                onChange={(e: ChangeEvent<HTMLInputElement>) => setAuctionDay(new Decimal(e.target.value).toNumber())}
              />
            </FormField>
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setActivateOpen(false)}>Cancel</Button>
            <Button
              variant="primary"
              onClick={handleActivate}
              disabled={!startDate || !auctionDay || activateMutation.isPending}
            >
              Confirm Activate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
